import { Button, Divider, Grid, Link, MenuItem, TextField, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import React from 'react';
import { Form, Formik } from 'formik';
import * as yup from 'yup';
import { KeyedMutator } from 'swr/_internal';
import {
  AccountData,
  AccountService,
  PartialLabelData,
  StorefrontStatus,
  VoucherRedemptionLinkTarget,
  VoucherSoldTypes,
} from '../api';
import { FormCheckbox } from '../experienceSettings/formComponents/FormCheckBox';
import CopyToClipboardButton from '../calendar/dialogs/CopyToClipboardButton';
import { accountSellsGiftCards, getGiftCardPageLink } from '../utils/AccountUtils';
import { FormTextField } from '../experienceSettings/formComponents/FormTextField';
import { FormLabelField } from '../experienceSettings/formComponents/FormLabelField';

type AccountVoucherSettingsProps = {
  account: AccountData;
  updateLocalAccount: KeyedMutator<AccountData>;
  setIsUpdatedSnackOpen: (value: boolean) => void;
};

type VoucherFormValues = {
  sold_types: VoucherSoldTypes;
  max_valid_days?: number;
  link_to_workshop_details: string;
  voucher_widget_title: PartialLabelData;
  voucher_widget_subtitle: PartialLabelData;
  preset_amounts: string;
  allow_custom_amount: boolean;
  min_amount?: number;
  max_amount?: number;
  validity_years?: number;
  redemption_link_target: VoucherRedemptionLinkTarget | '';
  redemption_storefront_id: string;
  redemption_custom_url: string;
  page_description: PartialLabelData;
};

const DEFAULT_MAX_VALID_DAYS = 365;

export const AccountVoucherSettings = ({
  account,
  updateLocalAccount,
  setIsUpdatedSnackOpen,
}: AccountVoucherSettingsProps) => {
  const { t } = useTranslation();
  const [isUpdatingSettings, setIsUpdatingSettings] = React.useState(false);
  const vouchersConfig = account.vouchers_config;
  const monetarySettings = account.monetary_voucher_settings;
  const publishedStorefronts = (account.storefronts || []).filter(
    (storefront) => storefront.status === StorefrontStatus.ACTIVE && storefront.short_code,
  );

  const initialValues: VoucherFormValues = {
    sold_types: vouchersConfig?.sold_types || VoucherSoldTypes.EXPERIENCE,
    max_valid_days: vouchersConfig?.max_valid_days ?? DEFAULT_MAX_VALID_DAYS,
    link_to_workshop_details: vouchersConfig?.link_to_workshop_details || '',
    voucher_widget_title: { en: '', he: '', ...vouchersConfig?.voucher_widget_title },
    voucher_widget_subtitle: { en: '', he: '', ...vouchersConfig?.voucher_widget_subtitle },
    preset_amounts: (monetarySettings?.preset_amounts || []).join(', '),
    allow_custom_amount: monetarySettings?.allow_custom_amount ?? true,
    min_amount: monetarySettings?.min_amount,
    max_amount: monetarySettings?.max_amount,
    validity_years: monetarySettings?.validity_years,
    redemption_link_target:
      monetarySettings?.redemption_link_target ||
      (publishedStorefronts.length > 0 ? VoucherRedemptionLinkTarget.STOREFRONT : ''),
    redemption_storefront_id: publishedStorefronts.some(
      (storefront) => storefront.id === monetarySettings?.redemption_storefront_id,
    )
      ? (monetarySettings?.redemption_storefront_id as string)
      : publishedStorefronts[0]?.id || '',
    redemption_custom_url: monetarySettings?.redemption_custom_url || '',
    page_description: { en: '', he: '', ...monetarySettings?.page_description },
  };

  const sellsExperience = (soldTypes: VoucherSoldTypes) =>
    soldTypes === VoucherSoldTypes.EXPERIENCE || soldTypes === VoucherSoldTypes.BOTH;
  const sellsMonetary = (soldTypes: VoucherSoldTypes) =>
    soldTypes === VoucherSoldTypes.MONETARY || soldTypes === VoucherSoldTypes.BOTH;

  const validationSchema = yup.object().shape({
    max_valid_days: yup.number().when('sold_types', {
      is: sellsExperience,
      then: (schema) => schema.required().min(1),
    }),
    min_amount: yup.number().when('sold_types', {
      is: sellsMonetary,
      then: (schema) => schema.required().min(1),
    }),
    max_amount: yup.number().when('sold_types', {
      is: sellsMonetary,
      then: (schema) => schema.required().min(yup.ref('min_amount')),
    }),
    validity_years: yup
      .number()
      .transform((value, originalValue) => (originalValue === '' ? undefined : value))
      .min(1),
    preset_amounts: yup.string().matches(/^\s*(\d+\s*(,\s*\d+\s*)*)?$/),
    redemption_link_target: yup.string().when('sold_types', {
      is: sellsMonetary,
      then: (schema) => schema.required(t('accountSettings.monetaryRedemptionLinkRequired')),
    }),
    redemption_storefront_id: yup.string().when(['sold_types', 'redemption_link_target'], {
      is: (soldTypes: VoucherSoldTypes, target: string) =>
        sellsMonetary(soldTypes) && target === VoucherRedemptionLinkTarget.STOREFRONT,
      then: (schema) => schema.required(t('accountSettings.monetaryRedemptionStorefrontRequired')),
    }),
    redemption_custom_url: yup.string().when(['sold_types', 'redemption_link_target'], {
      is: (soldTypes: VoucherSoldTypes, target: string) =>
        sellsMonetary(soldTypes) && target === VoucherRedemptionLinkTarget.CUSTOM_URL,
      then: (schema) =>
        schema
          .required(t('accountSettings.monetaryRedemptionCustomUrlRequired'))
          .url(t('accountSettings.monetaryRedemptionCustomUrlInvalid')),
    }),
  });

  const onSubmit = async (values: VoucherFormValues) => {
    setIsUpdatingSettings(true);
    const presetAmounts = values.preset_amounts
      .split(',')
      .map((amount) => parseInt(amount.trim(), 10))
      .filter((amount) => !Number.isNaN(amount));
    const updatedAccount = await AccountService.generalSettings({
      vouchers_config: {
        sold_types: values.sold_types,
        max_valid_days: values.max_valid_days as number,
        link_to_workshop_details: values.link_to_workshop_details || undefined,
        voucher_widget_title: values.voucher_widget_title,
        voucher_widget_subtitle: values.voucher_widget_subtitle,
      },
      monetary_voucher_settings: {
        preset_amounts: presetAmounts,
        allow_custom_amount: values.allow_custom_amount,
        min_amount: values.min_amount as number,
        max_amount: values.max_amount as number,
        validity_years: (values.validity_years || null) as number | undefined,
        redemption_link_target: values.redemption_link_target || undefined,
        redemption_storefront_id:
          values.redemption_link_target === VoucherRedemptionLinkTarget.STOREFRONT
            ? values.redemption_storefront_id
            : undefined,
        redemption_custom_url:
          values.redemption_link_target === VoucherRedemptionLinkTarget.CUSTOM_URL
            ? values.redemption_custom_url
            : undefined,
        page_description: values.page_description,
      },
    });
    await updateLocalAccount(updatedAccount);
    setIsUpdatingSettings(false);
    setIsUpdatedSnackOpen(true);
  };

  return (
    <Formik enableReinitialize initialValues={initialValues} validationSchema={validationSchema} onSubmit={onSubmit}>
      {({ handleSubmit, values, handleChange, errors, touched, submitCount }) => (
        <Form onSubmit={handleSubmit}>
          <Grid container flexDirection="column">
            <Grid item>
              <Typography variant="h4">{t('accountSettings.vouchers')}</Typography>
            </Grid>
            <Grid item mb={2}>
              <Typography variant="body1" color="text.secondary">
                {t('accountSettings.vouchersCaption')}
              </Typography>
            </Grid>
            <Grid item mb={2} md={4} xs={12}>
              <TextField
                select
                fullWidth
                name="sold_types"
                label={t('accountSettings.soldTypes.label')}
                value={values.sold_types}
                onChange={handleChange}>
                {Object.values(VoucherSoldTypes).map((soldType) => (
                  <MenuItem key={soldType} value={soldType}>
                    {t(`accountSettings.soldTypes.${soldType}`)}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            {sellsExperience(values.sold_types) && (
              <>
                <Grid item my={3}>
                  <Divider />
                </Grid>
                <Grid item>
                  <Typography variant="h5">{t('accountSettings.experienceVoucherTitle')}</Typography>
                </Grid>
                <Grid item mb={2}>
                  <Typography variant="body2" color="text.secondary">
                    {t('accountSettings.experienceVoucherCaption')}
                  </Typography>
                </Grid>
                <Grid item mb={2}>
                  <FormTextField
                    isFieldUpdate
                    fieldName="max_valid_days"
                    label={t('accountSettings.vouchersMaxValidDays')}
                    caption={t('accountSettings.vouchersMaxValidDaysCaption')}
                    type="number"
                    min={1}
                  />
                </Grid>
              </>
            )}
            {sellsMonetary(values.sold_types) && (
              <>
                <Grid item my={3}>
                  <Divider />
                </Grid>
                <Grid item>
                  <Typography variant="h5">{t('accountSettings.monetaryTitle')}</Typography>
                </Grid>
                <Grid item mb={2}>
                  <Typography variant="body2" color="text.secondary">
                    {t('accountSettings.monetaryCaption')}
                  </Typography>
                </Grid>
                {accountSellsGiftCards(account) && (
                  <Grid item container alignItems="center" gap={1} mb={2} flexWrap="nowrap">
                    <Typography variant="body2" fontWeight={600}>
                      {t('accountSettings.giftCardPageLink')}
                    </Typography>
                    <Link
                      href={getGiftCardPageLink(account.id)}
                      target="_blank"
                      rel="noopener"
                      variant="body2"
                      dir="ltr">
                      {getGiftCardPageLink(account.id)}
                    </Link>
                    <CopyToClipboardButton text={getGiftCardPageLink(account.id)} />
                  </Grid>
                )}
                <Grid item mb={2}>
                  <FormTextField
                    isFieldUpdate
                    fieldName="preset_amounts"
                    label={t('accountSettings.monetaryPresetAmounts')}
                    caption={t('accountSettings.monetaryPresetAmountsCaption')}
                    dir="ltr"
                  />
                </Grid>
                <Grid item>
                  <FormCheckbox
                    fieldName="allow_custom_amount"
                    disabled={false}
                    isFieldUpdate
                    label={t('accountSettings.monetaryAllowCustomAmount')}
                  />
                </Grid>
                {values.allow_custom_amount && (
                  <Grid container gap={2} mt={1} mb={2}>
                    <Grid item md={3} xs={12}>
                      <FormTextField
                        isFieldUpdate
                        fieldName="min_amount"
                        label={t('accountSettings.monetaryMinAmount')}
                        type="number"
                        min={1}
                        width="100%"
                      />
                    </Grid>
                    <Grid item md={3} xs={12}>
                      <FormTextField
                        isFieldUpdate
                        fieldName="max_amount"
                        label={t('accountSettings.monetaryMaxAmount')}
                        type="number"
                        min={1}
                        width="100%"
                      />
                    </Grid>
                  </Grid>
                )}
                <Grid item mt={values.allow_custom_amount ? 0 : 2}>
                  <FormTextField
                    isFieldUpdate
                    fieldName="validity_years"
                    label={t('accountSettings.monetaryValidityYears')}
                    caption={t('accountSettings.monetaryValidityLawNote')}
                    type="number"
                    min={1}
                  />
                </Grid>
                <Grid item mt={3}>
                  <Typography variant="h6">{t('accountSettings.monetaryRedemptionLink')}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('accountSettings.monetaryRedemptionLinkCaption')}
                  </Typography>
                </Grid>
                <Grid item mt={2} md={4} xs={12}>
                  <TextField
                    select
                    fullWidth
                    required
                    name="redemption_link_target"
                    label={t('accountSettings.monetaryRedemptionLinkTarget')}
                    value={values.redemption_link_target}
                    onChange={handleChange}
                    error={(submitCount > 0 || !!touched.redemption_link_target) && !!errors.redemption_link_target}
                    helperText={(submitCount > 0 || touched.redemption_link_target) && errors.redemption_link_target}>
                    <MenuItem value={VoucherRedemptionLinkTarget.STOREFRONT}>
                      {t('accountSettings.monetaryRedemptionLinkStorefront')}
                    </MenuItem>
                    <MenuItem value={VoucherRedemptionLinkTarget.CUSTOM_URL}>
                      {t('accountSettings.monetaryRedemptionLinkCustom')}
                    </MenuItem>
                  </TextField>
                </Grid>
                {values.redemption_link_target === VoucherRedemptionLinkTarget.STOREFRONT && (
                  <Grid item mt={2} md={4} xs={12}>
                    {publishedStorefronts.length > 0 ? (
                      <TextField
                        select
                        fullWidth
                        required
                        name="redemption_storefront_id"
                        label={t('accountSettings.monetaryRedemptionStorefront')}
                        value={values.redemption_storefront_id}
                        onChange={handleChange}
                        error={
                          (submitCount > 0 || !!touched.redemption_storefront_id) && !!errors.redemption_storefront_id
                        }
                        helperText={
                          (submitCount > 0 || touched.redemption_storefront_id) && errors.redemption_storefront_id
                        }>
                        {publishedStorefronts.map((storefront) => (
                          <MenuItem key={storefront.id} value={storefront.id}>
                            {storefront.name}
                          </MenuItem>
                        ))}
                      </TextField>
                    ) : (
                      <Typography variant="body2" color="error">
                        {t('accountSettings.monetaryRedemptionNoStorefronts')}
                      </Typography>
                    )}
                    <Link component={RouterLink} to="/dashboard/storefronts" variant="body2" mt={1} display="block">
                      {t('accountSettings.monetaryRedemptionManageStorefronts')}
                    </Link>
                  </Grid>
                )}
                {values.redemption_link_target === VoucherRedemptionLinkTarget.CUSTOM_URL && (
                  <Grid item mt={2}>
                    <FormTextField
                      isFieldUpdate
                      fieldName="redemption_custom_url"
                      label={`${t('accountSettings.monetaryRedemptionCustomUrl')} *`}
                      caption={t('accountSettings.monetaryRedemptionCustomUrlCaption')}
                      placeholder="https://"
                      dir="ltr"
                    />
                  </Grid>
                )}
                <Grid item mt={3}>
                  <FormLabelField
                    labelFieldName="page_description"
                    label={t('accountSettings.giftCardPageDescription')}
                    disabled={false}
                  />
                </Grid>
              </>
            )}
            <Grid container justifyContent="flex-end" mt={3}>
              <Button type="submit" variant="contained" disabled={isUpdatingSettings}>
                {t('save')}
              </Button>
            </Grid>
          </Grid>
        </Form>
      )}
    </Formik>
  );
};
